import { AggregationColor } from 'antd/lib/color-picker/color';

export class CustomAggregationColor extends AggregationColor {
  constructor(color: AggregationColor) {
    super(color); // ✅ Pass the color to the parent constructor
  }

  get RGBA() {
    const { r, g, b, a } = this['metaColor']; // Extract RGBA values
    return { r, g, b, a };
  }
}
const rgbaToHex = (r: number, g: number, b: number, a: number): string => {
  const alpha = Math.round(a * 255); // Convert alpha to range [0, 255]
  const hex = (n: number) => n.toString(16).padStart(2, '0'); // Ensure 2 digit hex format
  return `#${hex(r)}${hex(g)}${hex(b)}${hex(alpha)}`;
};

export const handleColorFormat = (newValue: AggregationColor) => {
  const customColor = new CustomAggregationColor(newValue);
  const { r, g, b, a } = customColor.RGBA;
  const rgbaFormat = rgbaToHex(r, g, b, a);
  return rgbaFormat;
};

export function makeTransparentColor(color: string): string {
  if (!color) return color;

  // If it's already rgba(...) format
  const rgbaMatch = color.match(/rgba\((\d+),\s*(\d+),\s*(\d+),\s*[\d.]+\)/);
  if (rgbaMatch) {
    const [_, r, g, b] = rgbaMatch;
    return `rgba(${r}, ${g}, ${b}, 0)`;
  }

  // If it's rgb(...) format
  const rgbMatch = color.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
  if (rgbMatch) {
    const [_, r, g, b] = rgbMatch;
    return `rgba(${r}, ${g}, ${b}, 0)`;
  }

  // If it's hex (#RRGGBB or #RGB)
  if (color.startsWith('#')) {
    let hex = color.slice(1);

    // Expand shorthand (#RGB → #RRGGBB)
    if (hex.length === 3) {
      hex = hex
        .split('')
        .map(c => c + c)
        .join('');
    }

    if (hex.length >= 6) {
      const r = parseInt(hex.substring(0, 2), 16);
      const g = parseInt(hex.substring(2, 4), 16);
      const b = parseInt(hex.substring(4, 6), 16);
      return `rgba(${r}, ${g}, ${b}, 0)`;
    }
  }

  // If it's a named color or unknown format, default to "transparent"
  return 'rgba(0,0,0,0)';
}
// ─────────────────────────────────────────────────────────────
// HTML class 기반 다크 모드 감지 — 공유 observer + 캐시된 값.
// `<html class="dark">` 토글을 MutationObserver로 한 번만 구독하고,
// getIsDarkMode()는 캐시된 boolean을 읽기만 한다 (DOM 접근 0회).
// useTheme의 fallback도 이 동일한 소스를 구독하여 observer 중복을 방지한다.
// ─────────────────────────────────────────────────────────────
let _isDarkCached: boolean | null = null;
let _observer: MutationObserver | null = null;
const _listeners = new Set<() => void>();

function ensureObserver(): void {
  if (_observer || typeof document === 'undefined') return;
  _isDarkCached = document.documentElement.classList.contains('dark');
  _observer = new MutationObserver(() => {
    const next = document.documentElement.classList.contains('dark');
    if (next === _isDarkCached) return;
    _isDarkCached = next;
    _listeners.forEach(l => l());
  });
  _observer.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['class'],
  });
}

export const getIsDarkMode = (): boolean => {
  if (typeof document === 'undefined') return false;
  if (_isDarkCached === null) {
    ensureObserver();
    if (_isDarkCached === null) {
      _isDarkCached = document.documentElement.classList.contains('dark');
    }
  }
  return _isDarkCached;
};

export const subscribeIsDarkMode = (listener: () => void): (() => void) => {
  ensureObserver();
  _listeners.add(listener);
  return () => {
    _listeners.delete(listener);
  };
};
